/**
 * Autoria da TARP (GUPM-292) — TS puro, sem React/Supabase/DOM.
 *
 * A TARP é colaborativa: qualquer usuário do app continua a de qualquer outro.
 * Este módulo só ORGANIZA para exibição as atribuições que vêm da função
 * `tarp_autoria` (derivadas do `audit_log`); ele não decide autoria.
 */

/** Momento atribuível no ciclo de vida de uma TARP. */
export type MomentoAutoria = 'iniciou' | 'editou' | 'abriu' | 'baixa';

/** Uma atribuição de ato a uma pessoa. `actorName` nulo = ato do sistema. */
export interface AutoriaEntrada {
  tarpId: string;
  momento: MomentoAutoria;
  actorName: string | null;
  ts: string;
}

/** Autoria pronta para o card: um rótulo por momento, na ordem do ciclo. */
export interface AutoriaExibida {
  momento: MomentoAutoria;
  label: string;
  nomes: string;
}

/**
 * Rótulo de quem não é pessoa. `audit_log.actor_id` é nulo em escrita de
 * cron/carga; omitir o momento nesse caso esconderia que o ato ocorreu, e deixar
 * vazio pareceria bug. A ausência de pessoa é informação — e se chama "Sistema".
 */
export const ATOR_SISTEMA = 'Sistema';

/**
 * Rótulo de pessoa não resolvida — `opened_by`/`closed_by` nulo, ou a conta saiu
 * do cadastro. Distinto de [[ATOR_SISTEMA]] de propósito: "Sistema" afirma que
 * quem agiu foi o cron/carga, e afirmar isso de um ato humano cuja conta sumiu
 * seria falso. Mantém o traço discreto que o card já usava (GUPM-190).
 */
export const AUTOR_NAO_RESOLVIDO = '—';

const LABEL: Record<MomentoAutoria, string> = {
  iniciou: 'Iniciada por',
  // GUPM-298: era "Editada por", com a lista de todos os editores. Dois problemas.
  // (1) A trilha sabe que houve ESCRITA, não que houve MUDANÇA — o trigger grava só
  // o snapshot da linha nova, sem payload_before/after —, então "editada" afirma
  // mais do que o dado sustenta. (2) Com 3 editores o card virava uma lista que
  // cresce e não responde o que o RT pergunta: quem mexeu por último, e quando.
  editou: 'Última atualização por',
  abriu: 'Aberta por',
  baixa: 'Baixa por',
};

/** Momentos exibidos com data/hora ao lado do nome. */
const COM_CARIMBO: ReadonlySet<MomentoAutoria> = new Set<MomentoAutoria>(['editou']);

/**
 * `dd/mm hh:mm` — sem ano e sem segundos: o card é dado de relance, e a TARP em
 * aberto é recente por definição. Sem locale para não depender do fuso do runtime
 * de teste: usa os campos locais do Date, que é o que o operador lê no relógio.
 */
export function carimbo(ts: string): string {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  const p2 = (n: number) => String(n).padStart(2, '0');
  return `${p2(d.getDate())}/${p2(d.getMonth() + 1)} ${p2(d.getHours())}:${p2(d.getMinutes())}`;
}

/** Ordem do ciclo de vida — não alfabética: o card conta uma história. */
const ORDEM: readonly MomentoAutoria[] = ['iniciou', 'editou', 'abriu', 'baixa'];

/**
 * Agrupa as entradas de UMA TARP em rótulos exibíveis.
 *
 * Momento sem entrada é OMITIDO — o card não anuncia uma abertura que não houve.
 *
 * GUPM-298: `editou` colapsa no toque MAIS RECENTE, com data/hora — "Última
 * atualização por Ana — 20/08 14:32". Antes listava todos os editores; a lista
 * crescia, repetia quem editou várias vezes e não respondia a pergunta do RT.
 * Os demais momentos são atos únicos e seguem só com o nome.
 */
export function autoriaParaExibicao(entradas: readonly AutoriaEntrada[]): AutoriaExibida[] {
  const out: AutoriaExibida[] = [];
  for (const momento of ORDEM) {
    const doMomento = entradas
      .filter((e) => e.momento === momento)
      .slice()
      .sort((a, b) => (a.ts < b.ts ? -1 : a.ts > b.ts ? 1 : 0));
    if (doMomento.length === 0) continue;
    if (COM_CARIMBO.has(momento)) {
      const ultimo = doMomento[doMomento.length - 1]!;
      const quando = carimbo(ultimo.ts);
      const nome = ultimo.actorName ?? ATOR_SISTEMA;
      out.push({
        momento,
        label: LABEL[momento],
        nomes: quando ? `${nome} — ${quando}` : nome,
      });
      continue;
    }
    out.push({
      momento,
      label: LABEL[momento],
      nomes: doMomento.map((e) => e.actorName ?? ATOR_SISTEMA).join(', '),
    });
  }
  return out;
}

/** Índice `tarpId` → entradas, para a lista distribuir sem varrer por card. */
export function agruparAutoriaPorTarp(
  entradas: readonly AutoriaEntrada[],
): Record<string, AutoriaEntrada[]> {
  const out: Record<string, AutoriaEntrada[]> = {};
  for (const e of entradas) (out[e.tarpId] ??= []).push(e);
  return out;
}
